import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import socket from "../../../context/socket";
import { FaPrint, FaRegFileExcel, FaRegFilePdf } from "react-icons/fa";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Swal from "sweetalert2";
import { TableOne } from "./TableOne";
import { TableTwo } from "./TableTwo";
import api from "../../../utils/api";
import TableDownloadable from "./TableDownloadable";

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export const Table = () => {
  const navigate = useNavigate();
  const roomId = new URLSearchParams(useLocation().search).get("clientId");
  const [selectedMember, setSelectedMember] = useState(null);
  const [bankDetails, setBankDetails] = useState([]);
  const [capitalGainResult, setCapitalGainResult] = useState({});
  const [result, setResult] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [transaction_period, setTransPeriod] = useState("0");
  const [asset_type, setAssetType] = useState("0");
  const [selectedYr, setSelectedYr] = useState("2025-2026");
  const [printMode, setPrintMode] = useState(false);
  const [forceExpand, setForceExpand] = useState(false);
  const [downloadMode, setDownloadMode] = useState(false);


  useEffect(() => {
    if (roomId) {
      getInvestor();
      socket.emit("join", roomId);
    }
  }, [roomId]);

  const getInvestor = async () => {
    try {
      Swal.fire({
        text: "Please wait...",
        imageUrl: "/assets/images/download.gif",
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
      const response = await api.get(`advisor/gethouseHoldPanInfo/${roomId}`);
      setBankDetails(response.data || []);
    } catch (error) {
      console.error("Error fetching AMC fund list:", error);
    } finally {
      Swal.close();
    }
  };

  useEffect(() => {
    if (bankDetails && bankDetails.length > 0 && !selectedMember) {
      setSelectedMember(bankDetails[0]);
    }
  }, [bankDetails, selectedMember]);

  const getHouseholdHead = () =>
    bankDetails.find(
      (item) => item.type === "Family Head" || item.type === "Individual"
    );

  const getPanVal = () => {
    const houseHoldHead = getHouseholdHead();
    if (selectedMember?.pan && selectedMember?.client_name) {
      return `${selectedMember.pan}_${selectedMember.client_name}`;
    } else if (houseHoldHead) {
      return `${houseHoldHead.pan}_${houseHoldHead.client_name}`;
    } else {
      return "unknown_Household";
    }
  };

  const getAllData = () => ({
    viewval: getPanVal(),
    year: selectedYr,
    asset_type: asset_type,
    transaction_period: transaction_period,
  });

  const sendTablePayload = (overrides = {}) => {
    const payload = {
      member: selectedMember?.client_name || "",
      pan: selectedMember?.pan || "",
      year: selectedYr,
      asset_type: asset_type,
      transaction_period: transaction_period,
      clientId: roomId,
      apply: false,
      ...overrides,
    };
    // debug log (remove in production)
    console.log("emit gateway payload:", payload);
    socket.emit("gateway", { tablePayload: payload, room: roomId });
  };

  const fetchCapitalGainsReport = async () => {
    const isHousehold = selectedMember?.pan === "all";

    const allData = isHousehold
      ? {
        groupId: bankDetails[0]?.groupId || "",
        memberId: "",
        clientid: roomId,
        year: selectedYr,
        asset_type,
        transaction_period,
      }
      : {
        groupId: selectedMember?.groupId || "",
        memberId: selectedMember?.wo_id || "",
        clientid: roomId,
        year: selectedYr,
        asset_type,
        transaction_period,
      };

    try {
      setIsLoading(true);
      setShowReport(false);
      Swal.fire({
        text: "Please wait...",
        imageUrl: "https://www.finedge.in/assets/images/download.gif",
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      // Emit apply = true with the latest values
      sendTablePayload({ apply: true });

      const response = await api.post(`advisor/capital_gains_report_wm`, allData);
      setCapitalGainResult(response.data);
      setResult(response.data.result);
      setShowReport(true);
    } catch (error) {
      console.error("Error fetching report:", error);
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: "Failed to load report. Please try again later.",
      });
    } finally {
      setIsLoading(false);
      Swal.close();
    }
  };

  // ----- Handlers: update state AND send payload with the new value directly -----
  const handleSelectChange = (e) => {
    const selectedId = e.target.value;
    let member;

    if (selectedId === "all") {
      member = { pan: "all", client_name: "All Household", _id: "all" };
    } else {
      member = bankDetails.find((m) => m._id === selectedId) || null;
    }

    setSelectedMember(member);
    // send updated member info immediately so socket gets latest
    sendTablePayload({
      pan: member?.pan || "",
      member: member?.client_name || "",
    });
  };

  const handleTransactionPeriodChange = (e) => {
    const value = e.target.value;
    setTransPeriod(value);
    sendTablePayload({ transaction_period: value });
  };

  const handleAssetTypeChange = (e) => {
    const value = e.target.value;
    setAssetType(value);
    sendTablePayload({ asset_type: value });
  };

  const handleYearChange = (e) => {
    const value = e.target.value;
    setSelectedYr(value);
    sendTablePayload({ year: value });
  };

  const handleNext = () => {
    const nextUrl = `/login?clientId=${roomId}`;
    socket.emit("navigate", { room: roomId, url: nextUrl });
    navigate(nextUrl);
  };

  const handleBack = () => {
    const prevUrl = `/portfolio-review?clientId=${roomId}`;
    socket.emit("navigate", { room: roomId, url: prevUrl });
    navigate(prevUrl);
  };

  /* downloadExcel, printReport, downloadPDF remain same as before - omitted here for brevity */
  // const downloadExcel = () => {
  //   const extracted = capitalGainResult?.extractedData || [];

  //   let csv =
  //     "Fund,Scheme,Folio,ISIN,Sub Type,Sub Date,Sub Amount,Sub Unit,Sub Cost,Redemp Type,Redemp Date,Redemp Amount,Redemp Unit,Redemp Price,Days,Index Cost,Gain/Loss\n";

  //   extracted.forEach((fundBlock) => {
  //     fundBlock.fundDetails.forEach((fund) => {
  //       fund.transactions.forEach((tx) => {
  //         csv +=
  //           [
  //             fundBlock.fund,
  //             fund.scheme,
  //             fund.folio,
  //             fund.isinNo,
  //             tx.subTransType,
  //             tx.subDate,
  //             tx.subAmount,
  //             tx.subUnit,
  //             tx.subCost,
  //             tx.redempTransType,
  //             tx.redempDate,
  //             tx.redempAmount,
  //             tx.redempUnit,
  //             tx.redempPrice,
  //             tx.days,
  //             tx.indexCost || "-",
  //             tx.gainLoss,
  //           ].join(",") + "\n";
  //       });
  //     });
  //   });

  //   const blob = new Blob([csv], { type: "text/csv" });
  //   const url = URL.createObjectURL(blob);
  //   const a = document.createElement("a");
  //   a.href = url;
  //   a.download = "Capital_Gain_Report.csv";
  //   a.click();
  // };


// const downloadExcel = () => {
//   const extracted = capitalGainResult?.result || [];
//   console.log("extracted", extracted)

//   const raw = capitalGainResult?.result || [];
//   const result = raw.length > 0 ? raw.slice(0, -1) : [];

//   console.log("TableOne result:", result);


//   // Map: category -> merged funds
//   const categoryMap = new Map();

//   result.forEach((fund) => {
//     const category = fund.Category || "Others";
//     if (!categoryMap.has(category)) categoryMap.set(category, new Map());

//     const fundMap = categoryMap.get(category);

//     const key = fund.Fund; // whole fund identified by fund name

//     fundMap.set(key, {
//       scheme: fund.Fund,
//       isinNo: fund.FundDetail[0]?.ISIN_NO || "",
//       folio: fund.FundDetail[0]?.Folio || "",
//       shortGain: Number(fund.FundTotal?.STGain || 0),
//       longGain: Number(fund.FundTotal?.LTGain || 0),
//       totalGain: Number(fund.FundTotal?.Gainloss || 0),
//     });
//   });


//   // Convert merged funds to arrays
//   const categoryFunds = {};
//   categoryMap.forEach((fundMap, category) => {
//     categoryFunds[category] = Array.from(fundMap.values()).map(f => ({ ...f, totalGain: f.shortGain + f.longGain }));
//   });

//   // Grand totals
//   const grandTotals = Object.values(categoryFunds)
//     .flat() // agar nested arrays hain
//     .reduce(
//       (acc, f) => {
//         acc.short += Number(f.shortGain || 0);
//         acc.long += Number(f.longGain || 0);
//         return acc;
//       },
//       { short: 0, long: 0 }
//     );




//   // Summary
//   const summaryName = result[0]?.FundDetail[0]?.Scheme || "Investor";
//   const memberType = "Individual";


//   let csv = "";

//   // === TOP SPACE / TITLE ===
//   csv += "\n\n"; // blank lines
//   csv += "Capital Gain Report\n\n";
//   csv += "====================================\n\n";
//   csv += ["Invester name", "F.Y."].join(",") + "\n";
//     csv += [`${ selectedMember?.client_name || ""} [${ selectedMember?.client_id || ""
//       }]`, `${selectedYr}`].join(",") + "\n\n";

//   csv += "====================================\n\n";
//   csv += "Generated On: " + new Date().toLocaleString() + "\n\n";

//   csv += "=========================================================\n\n";

//   csv += [
//     "Scheme Name",
//     "ISIN Number",
//     "Folio Number",
//     "Short Term Capital Gain",
//     "Long Term Capital Gain",
//     "Total Gain/Loss"
//   ].join(",") + "\n";

//   {
//     Object.entries(categoryFunds).map(([category, funds]) => {
//       console.log('funds', funds, category, categoryFunds);

//       const categoryTotals = funds.reduce(
//         (acc, f) => {
//           acc.short += f.shortGain;
//           acc.long += f.longGain;
//           return acc;
//         },
//         { short: 0, long: 0 }
//       );
//       const categoryTotal = categoryTotals.short + categoryTotals.long;
//       csv += `\n${category} — Funds \n\n`;
      
//       funds.map((fund, idx) => {
//         csv += [
//           fund.scheme,
//           fund.isinNo,
//           fund.folio,
//           Number(fund.shortGain || 0).toFixed(2),
//           Number(fund.longGain || 0).toFixed(2),
//           Number(fund.totalGain || 0).toFixed(2)
//         ].join(",") + "\n";
//       })
//       csv += "\n"; // space after category block
//       csv += [
//         `${category} Funds Total`,,,
//         Number(categoryTotals.short).toFixed(2),
//         Number(categoryTotals.long).toFixed(2),
//         Number(categoryTotal).toFixed(2),
//       ].join(",") + "\n";
//       csv += "\n\n"; // space after category block

//     })
//   }

//   csv += "=========================================================\n\n";

//   // === TABLE HEADER ===
//   csv += [
//     "Fund",
//     "Scheme",
//     "Folio",
//     "ISIN",
//     "Sub Type",
//     "Sub Date",
//     "Sub Amount",
//     "Sub Unit",
//     "Sub Cost",
//     "Redemp Type",
//     "Redemp Date",
//     "Redemp Amount",
//     "Redemp Unit",
//     "Redemp Price",
//     "Days",
//     "Index Cost",
//     "Gain/Loss"
//   ].join(",") + "\n";   // <--- extra space before rows

//   extracted.forEach((fundBlock) => {
//     console.log("fundBlock", fundBlock )
//     // CATEGORY HEADING WITH SPACING
//     csv += `\n\n${fundBlock.Fund} - ${fundBlock?.FundDetail?.[0]?.Scheme || ""}\n\n`;

//     fundBlock.FundDetail.forEach((fund) => {
//       fund.Transaction.forEach((tx) => {
//         csv += [
//           fundBlock.Fund,
//           fund.Scheme,
//           fund.Folio,
//           fund.ISIN_NO,
//           tx["Sub Trans Type"],
//           tx["Sub Date"],
//           tx["Sub Amount"],
//           tx["Sub Unit"],
//           tx["Sub Cost"],
//           tx["Redemp Trans Type"],
//           tx["Redemp Date"],
//           tx["Redemp Amount"],
//           tx["Redemp Unit"],
//           tx["Redemp Price"],
//           tx["Days"],
//           tx["Index Cost"] || "-",
//           tx["Gainloss"]
//         ].join(",") + "\n";
//       });
//     });

//     csv += [
//       `Total`,
//       ,
//       ,
//       ,
//       ,
//       ,
//       Number(fundBlock?.FundTotal?.SubAmount).toFixed(2),
//       ,
//       ,
//       ,
//       ,
//       Number(fundBlock?.FundTotal?.RedempAmount).toFixed(2),
//       ,,
//       "Total Gain/Loss",,
//       Number(fundBlock?.FundTotal?.Gainloss).toFixed(2)
//     ]

//     csv += "\n"; // space after category block
//   });

//   // Download file
//   const blob = new Blob([csv], { type: "text/csv" });
//   const url = URL.createObjectURL(blob);
//   const a = document.createElement("a");
//   a.href = url;
//   a.download = "Capital_Gain_Report.csv";
//   a.click();
// };

  const downloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Capital Gain Report");

    worksheet.properties.defaultRowHeight = 22;

    const extracted = capitalGainResult?.result || [];

    const raw = capitalGainResult?.result || [];
    const result = raw.length > 0 ? raw.slice(0, -1) : [];

    // ============================
    // Category Mapping
    // ============================

    const categoryMap = new Map();

    result.forEach((fund) => {
      const category = fund.Category || "Others";

      if (!categoryMap.has(category))
        categoryMap.set(category, new Map());

      const fundMap = categoryMap.get(category);

      fundMap.set(fund.Fund, {
        scheme: fund.Fund,
        isinNo: fund.FundDetail[0]?.ISIN_NO || "",
        folio: fund.FundDetail[0]?.Folio || "",
        shortGain: Number(fund.FundTotal?.STGain || 0),
        longGain: Number(fund.FundTotal?.LTGain || 0),
        totalGain: Number(fund.FundTotal?.Gainloss || 0),
      });
    });

    const categoryFunds = {};

    categoryMap.forEach((fundMap, category) => {
      categoryFunds[category] = Array.from(fundMap.values()).map((f) => ({
        ...f,
        totalGain: f.shortGain + f.longGain,
      }));
    });

    // ============================
    // TITLE
    // ============================

    worksheet.mergeCells("A1:F1");

    const title = worksheet.getCell("A1");

    title.value = "CAPITAL GAIN REPORT";

    title.font = {
      size: 18,
      bold: true,
      color: { argb: "FFFFFFFF" },
    };

    title.alignment = {
      horizontal: "center",
      vertical: "middle",
    };

    title.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: {
        argb: "1F4E78",
      },
    };

    worksheet.getRow(1).height = 28;

    // ============================
    // Investor Details
    // ============================

    worksheet.addRow([]);

    worksheet.addRow([
      "Investor Name",
      `${selectedMember?.client_name || ""} [${selectedMember?.client_id || ""
      }]`,
    ]);

    worksheet.addRow([
      "Financial Year",
      selectedYr,
    ]);

    worksheet.addRow([
      "Generated On",
      new Date().toLocaleString(),
    ]);

    worksheet.addRow([]);

    // ============================
    // Summary Header
    // ============================

    const headerRow = worksheet.addRow([
      "Scheme Name",
      "ISIN Number",
      "Folio Number",
      "Short Term Gain",
      "Long Term Gain",
      "Total Gain/Loss",
    ]);

    headerRow.eachCell((cell) => {
      cell.font = {
        bold: true,
        color: { argb: "FFFFFFFF" },
      };

      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb: "4472C4",
        },
      };

      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };

      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
        bottom: { style: "thin" },
      };
    });

    // ============================
    // Category Data
    // ============================

    Object.entries(categoryFunds).forEach(([category, funds]) => {

      // Category Heading

      const categoryRow = worksheet.addRow([`${category} Funds`]);

      worksheet.mergeCells(
        `A${categoryRow.number}:F${categoryRow.number}`
      );

      categoryRow.eachCell((cell) => {
        cell.font = {
          bold: true,
        };

        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: {
            argb: "C6E0B4",
          },
        };

        cell.alignment = {
          horizontal: "left",
        };

        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" },
          bottom: { style: "thin" },
        };
      });

      let short = 0;
      let long = 0;

      funds.forEach((fund) => {

        short += Number(fund.shortGain);
        long += Number(fund.longGain);

        const row = worksheet.addRow([
          fund.scheme,
          fund.isinNo,
          fund.folio,
          fund.shortGain,
          fund.longGain,
          fund.totalGain,
        ]);

        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
            bottom: { style: "thin" },
          };
        });
      });

      const totalRow = worksheet.addRow([
        `${category} Total`,
        "",
        "",
        short,
        long,
        short + long,
      ]);

      totalRow.eachCell((cell) => {

        cell.font = {
          bold: true,
        };

        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: {
            argb: "FFF2CC",
          },
        };

        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" },
          bottom: { style: "thin" },
        };
      });

      worksheet.addRow([]);
    });

    // Leave one blank line before transaction table
    worksheet.addRow([]);
    // ================================
    // Transaction Details Title
    // ================================

    worksheet.addRow([]);

    const txTitle = worksheet.addRow(["Transaction Details"]);

    worksheet.mergeCells(`A${txTitle.number}:Q${txTitle.number}`);

    txTitle.getCell(1).font = {
      bold: true,
      size: 16,
      color: { argb: "FFFFFFFF" },
    };

    txTitle.getCell(1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "1F4E78" },
    };

    txTitle.getCell(1).alignment = {
      horizontal: "center",
    };

    // =================================
    // Transaction Header
    // =================================

    const txHeader = worksheet.addRow([
      "Fund",
      "Scheme",
      "Folio",
      "ISIN",
      "Sub Type",
      "Sub Date",
      "Sub Amount",
      "Sub Unit",
      "Sub Cost",
      "Redemp Type",
      "Redemp Date",
      "Redemp Amount",
      "Redemp Unit",
      "Redemp Price",
      "Days",
      "Index Cost",
      "Gain/Loss",
    ]);

    txHeader.eachCell((cell) => {
      cell.font = {
        bold: true,
        color: { argb: "FFFFFFFF" },
      };

      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "4472C4" },
      };

      cell.alignment = {
        horizontal: "center",
        vertical: "middle",
      };

      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
        bottom: { style: "thin" },
      };
    });


    // =====================================
    // Transaction Data
    // =====================================

    extracted.forEach((fundBlock) => {

      const fundTitle = worksheet.addRow([
        `${fundBlock.Fund} - ${fundBlock?.FundDetail?.[0]?.Scheme || ""}`,
      ]);

      worksheet.mergeCells(`A${fundTitle.number}:Q${fundTitle.number}`);

      fundTitle.getCell(1).font = {
        bold: true,
        color: { argb: "000000" },
      };

      fundTitle.getCell(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb: "D9EAD3",
        },
      };

      fundBlock.FundDetail.forEach((fund) => {

        fund.Transaction.forEach((tx) => {

          const row = worksheet.addRow([
            fundBlock.Fund,
            fund.Scheme,
            fund.Folio,
            fund.ISIN_NO,
            tx["Sub Trans Type"],
            tx["Sub Date"],
            Number(tx["Sub Amount"] || 0),
            Number(tx["Sub Unit"] || 0),
            Number(tx["Sub Cost"] || 0),
            tx["Redemp Trans Type"],
            tx["Redemp Date"],
            Number(tx["Redemp Amount"] || 0),
            Number(tx["Redemp Unit"] || 0),
            Number(tx["Redemp Price"] || 0),
            tx["Days"],
            Number(tx["Index Cost"] || 0),
            Number(tx["Gainloss"] || 0),
          ]);

          row.eachCell((cell) => {

            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              right: { style: "thin" },
              bottom: { style: "thin" },
            };

            // Number formatting
            if (typeof cell.value === "number") {
              cell.numFmt = "#,##0.00";
            }
          });

          // Gain/Loss Color
          const gainCell = row.getCell(17);

          if (Number(gainCell.value) >= 0) {
            gainCell.font = {
              color: { argb: "008000" },
              bold: true,
            };
          } else {
            gainCell.font = {
              color: { argb: "FF0000" },
              bold: true,
            };
          }

        });

      });

      // ==========================
      // Fund Total
      // ==========================

      const totalRow = worksheet.addRow([
        "TOTAL",
        "",
        "",
        "",
        "",
        "",
        Number(fundBlock?.FundTotal?.SubAmount || 0),
        "",
        "",
        "",
        "",
        Number(fundBlock?.FundTotal?.RedempAmount || 0),
        "",
        "",
        "",
        "",
        Number(fundBlock?.FundTotal?.Gainloss || 0),
      ]);

      totalRow.eachCell((cell) => {

        cell.font = {
          bold: true,
        };

        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: {
            argb: "FFF2CC",
          },
        };

        cell.border = {
          top: { style: "medium" },
          left: { style: "thin" },
          right: { style: "thin" },
          bottom: { style: "medium" },
        };

        if (typeof cell.value === "number") {
          cell.numFmt = "#,##0.00";
        }
      });

      worksheet.addRow([]);
    });

    // =====================================
    // Auto Filter
    // =====================================

    txHeader.eachCell((cell, index) => {
      worksheet.getColumn(index).width = Math.max(
        worksheet.getColumn(index).width || 15,
        18
      );
    });

    worksheet.autoFilter = {
      from: {
        row: txHeader.number,
        column: 1,
      },
      to: {
        row: txHeader.number,
        column: 17,
      },
    };

    // =====================================
    // Download
    // =====================================

    const buffer = await workbook.xlsx.writeBuffer();

    saveAs(
      new Blob([buffer]),
      "Capital_Gain_Report.xlsx"
    );

  };


  const printReport = () => {
    setDownloadMode(true);
    const printContent = document.getElementById("htmlContainer");
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
    <html>
      <head>
        <title>Capital Gain Report</title>
        <link rel="stylesheet" href="/assets/css/bootstrap.min.css" />
        <style>
          body { font-family: sans-serif; padding: 20px; }
        </style>
      </head>
      <body>
      ${printContent?.innerHTML || ""}
      </body>
    </html>
  `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      setDownloadMode(false);
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const downloadPDF = () => {
    setDownloadMode(true);

    setTimeout(() => {
      const input = document.getElementById("htmlContainer");
      if (!input) return;

      html2canvas(input, { scale: 2 }).then((canvas) => {
        // const pdf = new jsPDF("l", "mm", "a4");

        // // === PAGE SIZE ===
        // const pageWidth = pdf.internal.pageSize.getWidth();     // 297mm
        // const pageHeight = pdf.internal.pageSize.getHeight();   // 210mm

        // // === CUSTOM MARGINS ===
        // const marginX = 15;   // left & right padding
        // const marginY = 15;   // top & bottom padding

        // // === USABLE CONTENT AREA ===
        // const contentWidth = pageWidth - marginX * 2;
        // const headerHeight = 45; // 35 se 45

        // const contentHeight = pageHeight - headerHeight - marginY;

        // ===================== PDF DIMENSIONS =====================
        const pdf = new jsPDF("l", "mm", "a4");

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        const marginX = 15;
        const marginBottom = 15;
        const headerHeight = 35;

        const printableWidth = pageWidth - marginX * 2;
        const printableHeight = pageHeight - headerHeight - marginBottom;

        // Canvas image ratio
        const imgWidth = printableWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // Convert printable height(mm) -> canvas pixels
        const pxPerMm = canvas.height / imgHeight;
        const pageCanvasHeight = Math.floor(printableHeight * pxPerMm);

        let page = 0;
        let cropY = 0;

        while (cropY < canvas.height) {

          // Remaining canvas height
          const cropHeight = Math.min(pageCanvasHeight, canvas.height - cropY);

          // Create temporary canvas
          const pageCanvas = document.createElement("canvas");
          pageCanvas.width = canvas.width;
          pageCanvas.height = cropHeight;

          const ctx = pageCanvas.getContext("2d");

          ctx.drawImage(
            canvas,
            0,
            cropY,
            canvas.width,
            cropHeight,
            0,
            0,
            canvas.width,
            cropHeight
          );

          const pageImg = pageCanvas.toDataURL("image/png");

          const pageImgHeight =
            (cropHeight * printableWidth) / canvas.width;

          if (page > 0) {
            pdf.addPage();
          }

          pdf.addImage(
            pageImg,
            "PNG",
            marginX,
            headerHeight,
            printableWidth,
            pageImgHeight
          );

          cropY += cropHeight;
          page++;
        }

        // ================= HEADER =================

        const totalPages = pdf.getNumberOfPages();

        for (let i = 1; i <= totalPages; i++) {

          pdf.setPage(i);

          pdf.setDrawColor(100);
          pdf.line(10, 5, pageWidth - 10, 5);

          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(18);
          pdf.setTextColor(52, 88, 140);
          pdf.text("Capital Gains Report - Mutual Fund", 10, 15);

          pdf.setFontSize(12);
          pdf.setTextColor(220, 0, 0);
          pdf.text(
            `${selectedMember?.client_name || ""} [${selectedMember?.client_id || ""}]`,
            10,
            25
          );

          pdf.rect(pageWidth / 2 - 40, 19, 80, 8);

          pdf.setFontSize(14);
          pdf.text(
            `F.Y. ${selectedYr}`,
            pageWidth / 2,
            25,
            { align: "center" }
          );

          pdf.setFontSize(10);
          pdf.setTextColor(0);

          pdf.text(
            `Pg. ${i} of ${totalPages}`,
            pageWidth - 10,
            15,
            { align: "right" }
          );

          pdf.text(
            new Date().toLocaleDateString("en-GB"),
            pageWidth - 10,
            25,
            { align: "right" }
          );

          pdf.line(10, 30, pageWidth - 10, 30);
        }

        pdf.save("Capital_Gain_Report.pdf");
        setDownloadMode(false);
      });
    }, 300);
  };





  return (
    <>
      <div className="container d-none d-md-block">
        <div className="row">
          <div className="col">
            <label className="fs-14 fw-normal clrlight">
              View capital Gain as
            </label>
            <select
              className="form-select w-100 w-md-25"
              onChange={handleSelectChange}
              value={selectedMember?._id || ""}
            >
              {bankDetails?.map((member) => (
                <option key={member._id} value={member._id}>
                  {member.client_name}
                </option>
              ))}
              <option value="all">All Household</option>
            </select>
          </div>

          <div className="col">
            <label className="fs-14 fw-normal clrlight">
              Transaction period
            </label>
            <select
              className="form-select"
              onChange={handleTransactionPeriodChange}
              value={transaction_period}
            >
              <option value="0">Both</option>
              <option value="1">Short Term</option>
              <option value="2">Long Term</option>
            </select>
          </div>

          <div className="col">
            <label className="fs-14 fw-normal clrlight">Assets type</label>
            <select
              className="form-select"
              onChange={handleAssetTypeChange}
              value={asset_type}
            >
              <option value="0">Both</option>
              <option value="1">Equity</option>
              <option value="2">Debt</option>
            </select>
          </div>

          <div className="col">
            <label className="fs-14 fw-normal clrlight">Year</label>
            <select
              className="form-select"
              onChange={handleYearChange}
              value={selectedYr}
            >
              {(() => {
                const currentMonth = new Date().getMonth() + 1;
                const currentYear = new Date().getFullYear();
                const currentFYStart = currentMonth >= 4 ? currentYear : currentYear - 1;

                const years = [];
                for (let y = currentFYStart; y >= 2015; y--) {
                  years.push(`${y}-${y + 1}`);
                }

                return years.map((fy) => (
                  <option key={fy} value={fy}>
                    {fy}
                  </option>
                ));
              })()}
            </select>
          </div>

          <div className="col">
            <button className="Applybtn mt-3 text-black" onClick={fetchCapitalGainsReport}>
              Apply
            </button>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 m-4">
          <FaPrint
            className="text-danger fs-3"
            style={{ cursor: "pointer" }}
            title="Print Capital Gain Report"
            onClick={printReport}
          />
          

          <FaRegFilePdf className="text-danger fs-3" style={{ cursor: "pointer" }} onClick={downloadPDF} title="Download PDF Capital Gain Report" />
          <FaRegFileExcel className="clrgreen fs-3" style={{ cursor: "pointer" }} title="Download Excel Capital Gain Report" onClick={downloadExcel} />
        </div>

        {showReport && (
          <div id="">
            <div className="row pt-4 pb-2">
              <div className="col">
                <p className="fs-21">
                  Capital Gain Summary of {selectedMember?.client_name} - {selectedMember?.pan}
                  {result["Member Type"]}
                </p>
              </div>
            </div>
            <TableOne capitalGainResult={capitalGainResult} />
            <TableTwo capitalGainResult={capitalGainResult} forceExpand={true} />

            <div className="row">
              <div className="col">
                <p className="fs-14">Disclaimer</p>
                <p className="fs-12 fw-normal">1. Investments in mutual funds and securities are subject to market risks...</p>
                <p className="fs-12 fw-normal">2. This statement is based on the information available with us...</p>
                <p className="fs-12 fw-normal">3. Tax is payable on your capital gain as per Income Tax Act...</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ======================= MOBILE VIEW ======================= */}
      <div className="container d-block d-md-none">

        {/* -------- FILTERS -------- */}
        <div className="mt-3">

          {/* Member Selection */}
          <label className="fs-14 fw-normal clrlight">View Capital Gain as</label>
          <select
            className="form-select mb-3 form-control-sm"
            onChange={handleSelectChange}
            value={selectedMember?._id || ""}
          >
            {bankDetails?.map((member) => (
              <option key={member._id} value={member._id}>
                {member.client_name}
              </option>
            ))}
            <option value="all">All Household</option>
          </select>

          {/* Transaction Period */}
          <label className="fs-14 fw-normal clrlight">Transaction Period</label>
          <select
            className="form-select mb-3"
            onChange={handleTransactionPeriodChange}
            value={transaction_period}
          >
            <option value="0">Both</option>
            <option value="1">Short Term</option>
            <option value="2">Long Term</option>
          </select>

          {/* Asset Type */}
          <label className="fs-14 fw-normal clrlight">Asset Type</label>
          <select
            className="form-select mb-3"
            onChange={handleAssetTypeChange}
            value={asset_type}
          >
            <option value="0">Both</option>
            <option value="1">Equity</option>
            <option value="2">Debt</option>
          </select>

          {/* Year */}
          <label className="fs-14 fw-normal clrlight">Year</label>
          <select
            className="form-select mb-3"
            onChange={handleYearChange}
            value={selectedYr}
          >
            {(() => {
              const currentMonth = new Date().getMonth() + 1;
              const currentYear = new Date().getFullYear();
              const currentFYStart = currentMonth >= 4 ? currentYear : currentYear - 1;

              const years = [];
              for (let y = currentFYStart; y >= 2015; y--) {
                years.push(`${y}-${y + 1}`);
              }

              return years.map((fy) => (
                <option key={fy} value={fy}>
                  {fy}
                </option>
              ));
            })()}
          </select>

          {/* Apply Button */}
          <button
            className="Applybtn text-black w-100 py-2 mt-2"
            onClick={fetchCapitalGainsReport}
          >
            Apply
          </button>
        </div>

        {/* -------- ACTION BUTTONS -------- */}
        <div className="d-flex justify-content-center gap-4 my-4">
          <FaPrint
            className="text-danger fs-3"
            style={{ cursor: "pointer" }}
            title="Print Capital Gain Report"
            onClick={printReport}
          />
          <FaRegFilePdf
            className="text-danger fs-3"
            style={{ cursor: "pointer" }}
            title="Download PDF"
            onClick={downloadPDF}
          />
          <FaRegFileExcel
            className="clrgreen fs-3"
            style={{ cursor: "pointer" }}
            title="Download Excel"
            onClick={downloadExcel}
          />
        </div>

        {/* -------- REPORT -------- */}
        {showReport && (
          <div >

            {/* Report Heading */}
            <p className="fs-18 fw-bold mt-3">
              Capital Gain Summary of {selectedMember?.client_name} - {selectedMember?.pan}
            </p>

            {/* MOBILE TABLES */}
            <TableOne capitalGainResult={capitalGainResult} />
            <TableTwo capitalGainResult={capitalGainResult} forceExpand={true} />

            {/* Disclaimer */}
            <div className="mt-3">
              <p className="fs-14 fw-bold">Disclaimer</p>

              <p className="fs-12 fw-normal">
                1. Investments in mutual funds and securities are subject to market risks.
              </p>
              <p className="fs-12 fw-normal">
                2. This statement is based on the information available with us.
              </p>
              <p className="fs-12 fw-normal">
                3. Tax is payable on your capital gain as per Income Tax Act.
              </p>
            </div>

          </div>
        )}
      </div>

      <div className={downloadMode ? "" : "d-none"}>
            <TableDownloadable
              capitalGainResult={capitalGainResult}
              selectedYr={selectedYr}
            />
          </div>

      <div className="container">
        <div className="row my-4">
          <div className="col text-end">
            <a href="javascript:void(0);" onClick={handleBack}>
              <img className="arrow_next1" src="assets/images/left-arrowpng.png" alt="" />
            </a>
            <a href="javascript:void(0);" className="ms-1" onClick={handleNext}>
              <img className="arrow_next" src="assets/images/right-arrow.svg" alt="" />
            </a>
          </div>
        </div>
      </div>
    </>
  );
};
